import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import DataTable, { type Column } from '../../components/DataTable';
import { useAuth } from '../../context/AuthContext';
import { changesApi } from '../../services/changesApi';
import type { ChangeListItem } from '../../types/changes';
import { getApiErrorMessage } from '../../utils/apiError';
import { labelChangeStatus, labelChangeType } from './changeLabels';
import { formatGoLiveWindow } from './goLiveWindow';
import ChangeCreateModal from './components/ChangeCreateModal';
import ChangeListFilters, { type ChangeListFilterValues } from './components/ChangeListFilters';
import { useActiveUsers } from './hooks/useActiveUsers';
import { useChangeCatalogs } from './hooks/useChangeCatalogs';
import { isChangeManager } from './changePermissions';
import { formatChangePublicId, parseChangePublicIdQuery } from './changePublicId';

const initialFilters = (): ChangeListFilterValues => ({
  search: '',
  status: '',
  type: '',
  categoryId: '',
  project: '',
  ownerId: '',
});

const Changes: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = isChangeManager(user);
  const { users } = useActiveUsers({ limit: 40 });
  // O filtro mostra também categoria desativada: mudanças antigas continuam nela.
  const { categories } = useChangeCatalogs({ onlyActive: false });
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<ChangeListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [filters, setFilters] = useState<ChangeListFilterValues>(initialFilters());
  const [listError, setListError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);

  const handleFiltersChange = (patch: Partial<ChangeListFilterValues>) => {
    setPage(1);
    setFilters((prev) => ({ ...prev, ...patch }));
  };

  const loadChanges = useCallback(async () => {
    setLoading(true);
    setListError(null);
    try {
      const skip = (page - 1) * itemsPerPage;
      const response = await changesApi.list({
        skip,
        limit: itemsPerPage,
        search: parseChangePublicIdQuery(filters.search.trim()) || undefined,
        status: filters.status || undefined,
        change_type: filters.type || undefined,
        category_id: filters.categoryId ? Number(filters.categoryId) : undefined,
        project: filters.project.trim() || undefined,
        owner_id: filters.ownerId ? Number(filters.ownerId) : undefined,
      });
      setItems(response.items);
      setTotal(response.total);
    } catch (error: unknown) {
      setListError(getApiErrorMessage(error, 'Não foi possível carregar as mudanças.'));
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [itemsPerPage, page, filters]);

  useEffect(() => {
    void loadChanges();
  }, [loadChanges]);

  const columns: Column<ChangeListItem>[] = useMemo(
    () => [
      {
        key: 'id',
        label: 'GMUD',
        sortable: true,
        render: (item) => formatChangePublicId(item.id),
      },
      { key: 'title', label: 'Título', sortable: true },
      {
        key: 'change_type',
        label: 'Tipo',
        render: (item) => labelChangeType(item.change_type),
      },
      {
        key: 'status',
        label: 'Status',
        sortable: true,
        render: (item) => labelChangeStatus(item.status),
      },
      { key: 'category', label: 'Categoria', render: (item) => item.category?.name || '—' },
      { key: 'project', label: 'Projeto' },
      {
        key: 'go_live_start_at',
        label: 'Go Live',
        render: (item) => formatGoLiveWindow(item.go_live_start_at, item.go_live_end_at),
      },
      {
        key: 'actions',
        label: 'Ações',
        render: (item) => (
          <Button size="sm" variant="outline-primary" onClick={() => navigate(`/changes/${item.id}`)}>
            Abrir
          </Button>
        ),
      },
    ],
    [navigate],
  );

  return (
    <div className="container-fluid py-3">
      <Card>
        <Card.Body>
          <div className="d-flex flex-wrap gap-2 justify-content-between align-items-center mb-3">
            <h4 className="mb-0">Gestão de Mudanças</h4>
            <div className="d-flex flex-wrap gap-2">
              {isAdmin ? (
                <>
                  <Button variant="outline-secondary" onClick={() => navigate('/changes/calendar')}>
                    Calendário
                  </Button>
                  <Button variant="outline-secondary" onClick={() => navigate('/changes/dashboard')}>
                    Indicadores
                  </Button>
                </>
              ) : null}
              <Button onClick={() => setShowCreateModal(true)}>Nova Mudança</Button>
            </div>
          </div>

          <ChangeListFilters
            values={filters}
            onChange={handleFiltersChange}
            users={users}
            categories={categories}
          />

          {listError ? (
            <Alert variant="danger" dismissible onClose={() => setListError(null)}>
              {listError}
            </Alert>
          ) : null}

          <DataTable<ChangeListItem>
            data={items}
            columns={columns}
            loading={loading}
            currentPage={page}
            itemsPerPage={itemsPerPage}
            totalItems={total}
            onPageChange={setPage}
            onItemsPerPageChange={setItemsPerPage}
            emptyMessage="Nenhuma mudança encontrada."
          />
        </Card.Body>
      </Card>

      <ChangeCreateModal
        show={showCreateModal}
        onHide={() => setShowCreateModal(false)}
        onCreated={loadChanges}
        users={[]}
        isAdmin={isAdmin}
        currentUserId={user?.id}
        canChooseRequester
      />
    </div>
  );
};

export default Changes;
